// Team and roles (TEAM-001, IA §2.1 /{org}/settings/team): members with role change and removal,
// pending invitations with revoke, and the invite dialog. Server rules this screen mirrors (the
// server still enforces them): the business owner changes only through an ownership transfer; you
// can't change your own role; a role change is sensitive (roles.manage, step-up); seats count
// pending invitations (BILLING-09 `staff`, 451 when full). Invitation delivery is manual on Workers
// Free, so the inviter gets a single-use link to share.
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDate } from "@ady/format";
import { i18n, t } from "@ady/i18n";
import {
  Badge,
  Banner,
  BusyRegion,
  Button,
  Checkbox,
  cx,
  Dialog,
  EmptyState,
  Field,
  focusFirstInvalid,
  Icon,
  Input,
  Select,
  SkeletonText,
  Tabs,
  useToast,
  type IconName,
} from "@ady/ui";
import { ApiError, api } from "../api/client";
import { errorMessage } from "../api/errors";
import { useIdempotencyKeys } from "../api/idempotency";
import { keys, useEntitlements, useInvitations, useMembers, useRoles } from "../api/queries";
import type {
  CreatedInvitation,
  Invitation,
  Member,
  QuotaExceededDetails,
  Role,
  StoreRef,
} from "../api/types";
import { isStepUpCancelled, stepUpErrorMessage, useStepUp } from "../auth/StepUp";
import { useShell } from "../shell/context";
import { can } from "../shell/nav";
import { CustomRoleDialog } from "./CustomRoleDialog";
import { TID } from "../testids";
import { LoadError, SettingsPage } from "./SettingsPage";
import { Note } from "./kit";

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** System roles read from the string layer; custom role names are merchant content. */
export function roleLabel(role: { key: string; name: string }): string {
  return role.key !== "custom" && i18n.has(`roles.${role.key}`)
    ? i18n.tDynamic(`roles.${role.key}`, "roles.custom")
    : role.name;
}
/** Select value: the preset key for system roles (stable across orgs), the ID for custom ones. */
const roleValue = (r: { id: string; key: string }) => (r.key === "custom" ? r.id : r.key);

/** The admin link for an invitation (the API path is `/invitations/<token>/accept`). */
export function invitationLink(acceptPath: string, origin = location.origin): string {
  const token = acceptPath.replace(/^\/invitations\//, "").replace(/\/accept$/, "");
  return `${origin}/invitations/${token}`;
}

function useInvalidateTeam(orgId: string) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: keys.members(orgId) }),
      qc.invalidateQueries({ queryKey: keys.invitations(orgId) }),
      qc.invalidateQueries({ queryKey: keys.entitlements(orgId) }),
      qc.invalidateQueries({ queryKey: keys.audit(orgId) }),
    ]);
}

export function TeamPage() {
  const { me, membership } = useShell();
  const orgId = membership.org.id;
  const canRead = can(membership, ["team.read"]);
  const canInvite = can(membership, ["team.invite"]);
  const canRemove = can(membership, ["team.remove"]);
  const canRoles = can(membership, ["roles.manage"]);
  const members = useMembers(orgId, canRead);
  const invitations = useInvitations(orgId, canRead);
  // GET /roles needs roles.manage (S, owner-only by default; domain/team routes.ts).
  const roles = useRoles(orgId, canRead && canRoles);
  const entitlements = useEntitlements(orgId, canRead);
  const [inviteOpen, setInviteOpen] = useState(false);
  // Compact page (owner, 2026-11-04): one summary line, then Members / Invitations / Roles as tabs.
  const [tab, setTab] = useState("members");
  const staff = entitlements.data?.quotas.staff;
  const active = (members.data?.data ?? []).filter((m) => m.status === "active");
  const pending = (invitations.data?.data ?? []).length;
  const roleList = roles.data?.data ?? [];

  const facts = [
    t("team.summary.members", { count: active.length }),
    t("team.summary.pending", { count: pending }),
    staff
      ? staff.limit === null
        ? t("team.seatsUnlimited", { used: staff.used })
        : t("team.seats", { used: staff.used, limit: staff.limit })
      : null,
  ].filter(Boolean);
  const full = Boolean(staff && staff.limit !== null && staff.used >= staff.limit);

  return (
    <SettingsPage
      title={t("team.title")}
      lead={t("team.lead", { org: membership.org.name })}
      anyOf={["team.read"]}
      testId={TID.teamPage}
      canonical="TEAM-001"
      actions={
        canInvite ? (
          <Button
            icon="plus"
            onClick={() => setInviteOpen(true)}
            data-testid={TID.inviteButton}
            availability={
              canRoles ? undefined : { kind: "permission-denied", reason: t("team.rolesNeedOwner") }
            }
          >
            {t("team.invite")}
          </Button>
        ) : null
      }
    >
      <p
        className={cx(
          "flex flex-wrap items-center gap-x-3 gap-y-1 text-xs",
          full ? "text-warning-fg" : "text-fg-secondary",
        )}
        data-testid="team-summary"
      >
        <Icon name="users" className="text-fg-tertiary" />
        {facts.map((f, i) => (
          <span key={i} className="flex items-center gap-3">
            {i > 0 ? <span aria-hidden="true">·</span> : null}
            {f}
          </span>
        ))}
      </p>
      <Tabs
        label={t("team.title")}
        value={tab}
        onValueChange={setTab}
        items={[
          {
            id: "members",
            label: t("team.membersTitle"),
            panel: (
              <MembersSection
                orgId={orgId}
                orgName={membership.org.name}
                meUserId={me.user.id}
                members={members}
                roles={roles.data?.data}
                canChangeRoles={canRoles}
                canRemove={canRemove}
              />
            ),
          },
          {
            id: "invitations",
            label: t("team.invitationsTitle"),
            ...(pending ? { count: pending } : {}),
            panel: (
              <InvitationsSection
                orgId={orgId}
                invitations={invitations}
                roles={roles.data?.data}
                canRevoke={canInvite}
              />
            ),
          },
          ...(canRoles
            ? [
                {
                  id: "roles",
                  label: t("team.rolesTab"),
                  panel: (
                    <RolesPanel
                      orgId={orgId}
                      roles={roleList}
                      members={members.data?.data ?? []}
                      loading={roles.isPending}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />
      {canInvite && canRoles ? (
        <InviteDialog
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          orgId={orgId}
          planName={membership.plan.name}
          roles={roles.data?.data}
          rolesError={roles.isError}
          stores={membership.stores}
        />
      ) : null}
    </SettingsPage>
  );
}

// ---------------------------------------------------------------- compact rows

/** The list frame every tab uses: a header row on wide screens, rows that stack on phones. */
const LIST = "flex flex-col divide-y divide-subtle rounded-sm border border-card bg-surface";
const HEAD =
  "hidden gap-x-4 bg-sunken px-3 py-1.5 text-2xs font-semibold tracking-wide text-fg-tertiary uppercase md:grid";
const ROW = "flex flex-col gap-2 px-3 py-2.5 md:grid md:items-center md:gap-x-4";
const MEMBER_COLS = "md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_7rem_15rem]";
const INVITE_COLS = "md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,0.8fr)_7rem_15rem]";
const ROLE_COLS = "md:grid-cols-[minmax(0,1.8fr)_6rem_minmax(0,1fr)_5rem_10rem]";

/** A round initial for a person (or an envelope for an invitation). */
function Initial({ name, icon }: { name?: string; icon?: IconName }) {
  return (
    <span
      aria-hidden="true"
      className="ady-bg-brand-tint flex size-8 flex-none items-center justify-center rounded-full text-xs font-semibold text-link"
    >
      {icon ? <Icon name={icon} /> : (Array.from(name ?? "?")[0] ?? "?").toUpperCase()}
    </span>
  );
}

/** A label shown only on phones, where the header row is hidden. */
function Cell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-center gap-2 text-sm md:block">
      <span className="w-20 flex-none text-2xs text-fg-tertiary md:hidden">{label}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------- members

function MembersSection({
  orgId,
  orgName,
  meUserId,
  members,
  roles,
  canChangeRoles,
  canRemove,
}: {
  orgId: string;
  orgName: string;
  meUserId: string;
  members: ReturnType<typeof useMembers>;
  roles: Role[] | undefined;
  canChangeRoles: boolean;
  canRemove: boolean;
}) {
  const toast = useToast();
  const stepUp = useStepUp();
  const invalidate = useInvalidateTeam(orgId);
  const [removing, setRemoving] = useState<Member | null>(null);
  // Edit a custom role's permissions from a member's row (owner, 2026-11-04).
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const nameOf = (m: Member) => m.display_name ?? t("team.nameMissing");

  const save = useMutation({
    mutationFn: ({ member, role }: { member: Member; role: Role }) =>
      stepUp(() => api.changeMemberRole(orgId, member.id, role.id)),
    onSuccess: async (_res, { member, role }) => {
      toast.show({
        tone: "success",
        title: t("team.roleSaved", { name: nameOf(member), role: roleLabel(role) }),
      });
      await invalidate();
    },
    onError: (e) => {
      const msg = stepUpErrorMessage(e);
      if (msg) toast.show({ tone: "error", title: msg });
    },
  });
  const remove = useMutation({
    mutationFn: (member: Member) => api.removeMember(orgId, member.id),
    onSuccess: async (_res, member) => {
      setRemoving(null);
      toast.show({ tone: "success", title: t("team.removed", { name: nameOf(member) }) });
      await invalidate();
    },
    onError: (e) => toast.show({ tone: "error", title: errorMessage(e) }),
  });

  const rows = members.data?.data ?? [];
  return (
    <section id="team-members" className="flex flex-col gap-3 pt-3">
      {members.isError ? (
        <LoadError error={members.error} onRetry={() => void members.refetch()} />
      ) : (
        <BusyRegion
          busy={members.isPending}
          skeleton={<SkeletonText lines={4} />}
          data-testid={members.isPending ? TID.loadingState : undefined}
        >
          <div className={LIST}>
            <div className={cx(HEAD, MEMBER_COLS)} aria-hidden="true">
              <span>{t("team.cols.person")}</span>
              <span>{t("team.cols.role")}</span>
              <span>{t("team.cols.joined")}</span>
              <span className="text-end">{t("team.cols.actions")}</span>
            </div>
            <ul
              aria-label={t("team.membersListLabel")}
              className="flex flex-col divide-y divide-subtle"
            >
              {rows.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  isMe={m.user_id === meUserId}
                  roles={roles}
                  canChangeRoles={canChangeRoles}
                  canRemove={canRemove}
                  saving={save.isPending && save.variables?.member.id === m.id}
                  onSave={(role) => save.mutate({ member: m, role })}
                  onRemove={() => setRemoving(m)}
                  onEditRole={setEditingRole}
                />
              ))}
            </ul>
          </div>
          {rows.length <= 1 ? (
            <EmptyState
              size="inline"
              headingLevel={3}
              title={t("team.onlyYouTitle")}
              body={t("team.onlyYouBody")}
              className="pt-4"
              data-testid={TID.emptyState}
            />
          ) : null}
        </BusyRegion>
      )}
      <CustomRoleDialog
        open={editingRole !== null}
        onClose={() => setEditingRole(null)}
        orgId={orgId}
        role={editingRole}
        onCreated={(r) => {
          toast.show({ tone: "success", title: t("team.customRole.updated", { name: r.name }) });
          void invalidate();
        }}
      />
      <Dialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={t("team.removeTitle", { name: removing ? nameOf(removing) : "" })}
        description={t("team.removeBody", { org: orgName })}
        confirm={{
          label: t("team.remove"),
          variant: "danger",
          loading: remove.isPending,
          onClick: () => removing && remove.mutate(removing),
          "data-testid": TID.confirmDialogConfirm,
        }}
      />
    </section>
  );
}

function MemberRow({
  member,
  isMe,
  roles,
  canChangeRoles,
  canRemove,
  saving,
  onSave,
  onRemove,
  onEditRole,
}: {
  member: Member;
  isMe: boolean;
  roles: Role[] | undefined;
  canChangeRoles: boolean;
  canRemove: boolean;
  saving: boolean;
  onSave: (role: Role) => void;
  onRemove: () => void;
  /** Opens the permission editor for a custom role. */
  onEditRole: (role: Role) => void;
}) {
  const current = roleValue(member.role);
  const [value, setValue] = useState(current);
  useEffect(() => setValue(current), [current]);
  const name = member.display_name ?? t("team.nameMissing");
  const manageable = !isMe && !member.is_primary_owner;
  const options = useMemo(
    () =>
      (roles ?? [])
        // Ownership moves only through a transfer; keep the current role selectable for display.
        .filter((r) => r.key !== "owner" || roleValue(r) === current)
        .map((r) => ({ value: roleValue(r), label: roleLabel(r) })),
    [roles, current],
  );
  const chosen = roles?.find((r) => roleValue(r) === value);
  const editable = manageable && canChangeRoles && options.length > 0;
  // The custom role shown in the menu can have its permissions edited (preset roles are fixed).
  const customChosen = chosen && chosen.key === "custom" && !chosen.is_system ? chosen : null;
  const changed = value !== current && Boolean(chosen);
  return (
    <li data-testid={TID.memberRow} data-member={member.id} className={cx(ROW, MEMBER_COLS)}>
      <div className="flex min-w-0 items-center gap-2.5">
        <Initial name={name} />
        <div className="flex min-w-0 flex-col">
          <p className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span
              className={cx(
                "truncate text-sm",
                member.display_name ? "font-semibold" : "text-fg-secondary",
              )}
              data-content={member.display_name ? "user" : undefined}
            >
              {name}
            </span>
            {isMe ? <Badge tone="action">{t("team.you")}</Badge> : null}
            {member.is_primary_owner ? (
              <Badge tone="neutral" icon="key">
                {t("team.primaryOwner")}
              </Badge>
            ) : null}
            {member.status === "suspended" ? (
              <Badge tone="warning">{t("team.suspended")}</Badge>
            ) : null}
          </p>
        </div>
      </div>
      <Cell label={t("team.cols.role")}>
        {editable ? (
          <div className="flex items-center gap-1.5">
            <div className="min-w-0 flex-1">
              <Select
                aria-label={t("team.roleFor", { name })}
                options={options}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                data-testid={TID.memberRoleSelect}
              />
            </div>
            {changed ? (
              <Button
                size="md"
                loading={saving}
                loadingLabel={t("team.savingRole")}
                onClick={() => chosen && onSave(chosen)}
                data-testid={TID.memberRoleSave}
              >
                {t("team.saveShort")}
              </Button>
            ) : null}
          </div>
        ) : (
          <span className="text-sm">{roleLabel(member.role)}</span>
        )}
      </Cell>
      <Cell label={t("team.cols.joined")}>
        <span className="text-xs text-fg-secondary">
          {member.created_at ? formatDate(member.created_at, { style: "medium" }) : "—"}
        </span>
      </Cell>
      <div className="flex flex-wrap items-center gap-1 md:flex-nowrap md:justify-end">
        {editable && customChosen ? (
          <Button
            variant="ghost"
            size="md"
            icon="shield"
            onClick={() => onEditRole(customChosen)}
            data-testid="member-edit-permissions"
          >
            {t("team.customRole.editShort")}
          </Button>
        ) : null}
        {manageable && canRemove ? (
          <Button
            icon="x"
            variant="ghost"
            size="md"
            onClick={onRemove}
            data-testid={TID.memberRemove}
          >
            {t("team.remove")}
          </Button>
        ) : null}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------- invitations

function InvitationsSection({
  orgId,
  invitations,
  roles,
  canRevoke,
}: {
  orgId: string;
  invitations: ReturnType<typeof useInvitations>;
  roles: Role[] | undefined;
  canRevoke: boolean;
}) {
  const toast = useToast();
  const invalidate = useInvalidateTeam(orgId);
  const [revoking, setRevoking] = useState<Invitation | null>(null);
  const revoke = useMutation({
    mutationFn: (inv: Invitation) => api.revokeInvitation(orgId, inv.id),
    onSuccess: async () => {
      setRevoking(null);
      toast.show({ tone: "success", title: t("team.revoked") });
      await invalidate();
    },
    onError: (e) => toast.show({ tone: "error", title: errorMessage(e) }),
  });
  const rows = invitations.data?.data ?? [];
  const [editing, setEditing] = useState<Role | null>(null);
  const customRoleOf = (id: string) =>
    roles?.find((r) => r.id === id && r.key === "custom" && !r.is_system);
  const roleName = (id: string) => {
    const r = roles?.find((x) => x.id === id);
    return r ? roleLabel(r) : roles ? t("team.unknownRole") : "—";
  };
  return (
    <section id="team-invitations" className="flex flex-col gap-3 pt-3">
      {invitations.isError ? (
        <LoadError error={invitations.error} onRetry={() => void invitations.refetch()} />
      ) : (
        <BusyRegion busy={invitations.isPending} skeleton={<SkeletonText lines={2} />}>
          {rows.length === 0 ? (
            <EmptyState
              size="inline"
              headingLevel={3}
              title={t("team.invitationsEmptyTitle")}
              body={t("team.invitationsEmptyBody")}
            />
          ) : (
            <div className={LIST}>
              <div className={cx(HEAD, INVITE_COLS)} aria-hidden="true">
                <span>{t("team.cols.email")}</span>
                <span>{t("team.cols.role")}</span>
                <span>{t("team.cols.stores")}</span>
                <span>{t("team.cols.expires")}</span>
                <span className="text-end">{t("team.cols.actions")}</span>
              </div>
              <ul
                aria-label={t("team.invitationsListLabel")}
                className="flex flex-col divide-y divide-subtle"
              >
                {rows.map((inv) => {
                  const custom = customRoleOf(inv.role_id);
                  return (
                    <li
                      key={inv.id}
                      data-testid={TID.invitationRow}
                      className={cx(ROW, INVITE_COLS)}
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <Initial icon="mail" />
                        <span className="truncate text-sm font-semibold break-all">
                          {inv.target}
                        </span>
                      </div>
                      <Cell label={t("team.cols.role")}>
                        <span className="text-sm">{roleName(inv.role_id)}</span>
                      </Cell>
                      <Cell label={t("team.cols.stores")}>
                        <span className="text-xs text-fg-secondary">
                          {inv.scopes.length
                            ? t("team.someStores", { count: inv.scopes.length })
                            : t("team.allStores")}
                        </span>
                      </Cell>
                      <Cell label={t("team.cols.expires")}>
                        <span className="text-xs text-fg-secondary">
                          {inv.expires_at ? formatDate(inv.expires_at, { style: "medium" }) : "—"}
                        </span>
                      </Cell>
                      <div className="flex flex-wrap items-center gap-1 md:flex-nowrap md:justify-end">
                        {/* A custom role's permissions (owner, 2026-11-04); preset roles are fixed.
                            Roles only load for someone who may manage them. */}
                        {custom ? (
                          <Button
                            variant="ghost"
                            size="md"
                            icon="shield"
                            onClick={() => setEditing(custom)}
                            data-testid="invitation-edit-permissions"
                          >
                            {t("team.customRole.editShort")}
                          </Button>
                        ) : null}
                        {canRevoke ? (
                          <Button
                            variant="ghost"
                            size="md"
                            icon="x"
                            onClick={() => setRevoking(inv)}
                            data-testid={TID.invitationRevoke}
                          >
                            {t("team.revoke")}
                          </Button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </BusyRegion>
      )}
      <CustomRoleDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        orgId={orgId}
        role={editing}
        onCreated={(r) =>
          toast.show({ tone: "success", title: t("team.customRole.updated", { name: r.name }) })
        }
      />
      <Dialog
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        title={t("team.revokeTitle", { target: revoking?.target ?? "" })}
        description={t("team.revokeBody")}
        confirm={{
          label: t("team.revoke"),
          variant: "danger",
          loading: revoke.isPending,
          onClick: () => revoking && revoke.mutate(revoking),
          "data-testid": TID.confirmDialogConfirm,
        }}
      />
    </section>
  );
}

// ---------------------------------------------------------------- invite dialog

type InviteErrors = { email?: string; role?: string };

function InviteDialog({
  open,
  onClose,
  orgId,
  planName,
  roles,
  rolesError,
  stores,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  planName: string;
  roles: Role[] | undefined;
  rolesError: boolean;
  stores: StoreRef[];
}) {
  const toast = useToast();
  const stepUpInvite = useStepUp();
  const invalidate = useInvalidateTeam(orgId);
  const keyFor = useIdempotencyKeys();
  const formRef = useRef<HTMLFormElement>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [scopes, setScopes] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<InviteErrors>({});
  const [formError, setFormError] = useState<string>();
  const [quota, setQuota] = useState<QuotaExceededDetails | null>(null);
  const [created, setCreated] = useState<CreatedInvitation | null>(null);
  const [busy, setBusy] = useState(false);
  // Custom permissions (owner, 2026-11-04): a new custom role, chosen here once saved.
  const [customOpen, setCustomOpen] = useState(false);
  const [newRole, setNewRole] = useState<Role | null>(null);

  const reset = () => {
    setEmail("");
    setRole("");
    setScopes(new Set());
    setErrors({});
    setFormError(undefined);
    setQuota(null);
    setCreated(null);
  };
  useEffect(() => {
    if (!open) reset();
  }, [open]);

  // The role just saved shows at once, before the roles list reloads.
  const roleList = [
    ...(roles ?? []),
    ...(newRole && !(roles ?? []).some((r) => r.id === newRole.id) ? [newRole] : []),
  ];
  const options = roleList
    .filter((r) => r.key !== "owner")
    .map((r) => ({ value: roleValue(r), label: roleLabel(r) }));

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    setFormError(undefined);
    setQuota(null);
    const target = email.trim();
    const chosen = roleList.find((r) => roleValue(r) === role);
    const next: InviteErrors = {};
    if (!EMAIL.test(target)) next.email = t("team.inviteDialog.emailInvalid");
    if (!chosen) next.role = t("team.inviteDialog.roleRequired");
    setErrors(next);
    if (next.email || next.role || !chosen) {
      requestAnimationFrame(() => focusFirstInvalid(formRef.current));
      return;
    }
    const body = {
      channel: "email" as const,
      target,
      role_id: chosen.id,
      scopes: [...scopes].sort(),
    };
    setBusy(true);
    try {
      // A role with sensitive permissions may need "confirm it's you" first (stepUp retries).
      const res = await stepUpInvite(() => api.invite(orgId, body, keyFor({ orgId, ...body })));
      setCreated(res);
      await invalidate();
    } catch (err) {
      if (isStepUpCancelled(err)) return;
      if (err instanceof ApiError && err.code === "entitlement.quota_exceeded") {
        setQuota((err.details ?? {}) as QuotaExceededDetails);
      } else if (err instanceof ApiError && err.code === "validation.already_invited") {
        setErrors({ email: t("team.inviteDialog.alreadyInvited") });
      } else if (
        err instanceof ApiError &&
        err.code.startsWith("validation.") &&
        err.fields?.target
      ) {
        setErrors({ email: t("team.inviteDialog.emailInvalid") });
      } else if (err instanceof ApiError && err.code === "authz.forbidden") {
        setFormError(t("team.inviteDialog.escalation"));
      } else {
        setFormError(errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const link = created ? invitationLink(created.accept_path) : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.show({ tone: "success", title: t("team.inviteDialog.copied") });
    } catch {
      toast.show({ tone: "error", title: t("team.inviteDialog.copyFailed") });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        created
          ? created.delivery === "email"
            ? t("team.inviteDialog.sentTitle")
            : t("team.inviteDialog.createdTitle")
          : t("team.inviteDialog.title")
      }
      description={created ? undefined : t("team.inviteDialog.lead")}
      data-testid={TID.inviteDialog}
      dismissLabel={created ? t("team.inviteDialog.done") : undefined}
      confirm={
        created
          ? undefined
          : {
              label: t("team.inviteDialog.submit"),
              loading: busy,
              loadingLabel: t("team.inviteDialog.submitting"),
              onClick: () => void submit(),
              "data-testid": TID.inviteSubmit,
            }
      }
    >
      {created ? (
        <div className="flex flex-col gap-4">
          <p className="text-md">
            {created.delivery === "email"
              ? t("team.inviteDialog.sentBody", { target: created.target })
              : t("team.inviteDialog.createdBody", { target: created.target })}
          </p>
          <Field
            label={t("team.inviteDialog.linkLabel")}
            help={t("team.inviteDialog.linkHelp", {
              target: created.target,
              when: created.expires_at ? formatDate(created.expires_at, { style: "medium" }) : "",
            })}
          >
            <Input
              readOnly
              value={link}
              onFocus={(e) => e.currentTarget.select()}
              className="ady-ledger text-sm"
              data-testid={TID.inviteLink}
            />
          </Field>
          <div className="flex flex-wrap gap-3">
            <Button icon="link" size="md" onClick={copy} data-testid={TID.inviteLinkCopy}>
              {t("team.inviteDialog.copy")}
            </Button>
            <Button icon="plus" variant="link" onClick={reset}>
              {t("team.inviteDialog.inviteAnother")}
            </Button>
          </div>
        </div>
      ) : (
        <form ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4 pt-4">
          {quota ? (
            <Banner kind="plan-locked" title={t("team.quota.title")} data-testid={TID.quotaBanner}>
              <p>
                {typeof quota.limit === "number" && typeof quota.used === "number"
                  ? t("team.quota.body", { plan: planName, limit: quota.limit, used: quota.used })
                  : t("team.quota.bodyGeneric")}
              </p>
              <p className="pt-1 text-fg-secondary">{t("team.quota.later")}</p>
            </Banner>
          ) : null}
          {formError ? <Banner kind="error" title={formError} data-testid={TID.formError} /> : null}
          {rolesError ? <Banner kind="error" title={t("team.rolesLoadFailed")} /> : null}
          <Field
            label={t("team.inviteDialog.emailLabel")}
            help={t("team.inviteDialog.emailHelp")}
            error={errors.email}
            required
          >
            <Input
              type="email"
              inputMode="email"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid={TID.inviteEmailInput}
            />
          </Field>
          <Field label={t("team.inviteDialog.roleLabel")} error={errors.role} required>
            <Select
              options={options}
              placeholder={t("team.inviteDialog.rolePlaceholder")}
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid={TID.inviteRoleSelect}
            />
          </Field>
          {/* Only someone who may manage roles (the owner) sees roles at all, so the list being
              loaded is the permission check here; the server checks again. */}
          {roles ? (
            <div className="-mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
              <Button
                icon="shield"
                variant="link"
                onClick={() => setCustomOpen(true)}
                data-testid="invite-custom-permissions"
              >
                {t("team.inviteDialog.customButton")}
              </Button>
              <span className="text-xs text-fg-tertiary">{t("team.inviteDialog.customHint")}</span>
            </div>
          ) : null}
          {stores.length > 1 ? (
            <fieldset className="flex flex-col gap-1">
              <legend className="pb-1 text-sm font-medium">
                {t("team.inviteDialog.storesLegend")}
                <span className="ms-2 font-normal text-fg-tertiary">{t("common.optional")}</span>
              </legend>
              <p className="pb-1 text-xs text-fg-secondary">{t("team.inviteDialog.storesHelp")}</p>
              {stores.map((s) => (
                <Checkbox
                  key={s.id}
                  label={<span data-content="user">{s.name}</span>}
                  checked={scopes.has(s.id)}
                  onChange={(e) =>
                    setScopes((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(s.id);
                      else next.delete(s.id);
                      return next;
                    })
                  }
                />
              ))}
            </fieldset>
          ) : null}
        </form>
      )}
      <CustomRoleDialog
        open={customOpen}
        onClose={() => setCustomOpen(false)}
        orgId={orgId}
        onCreated={(r) => {
          setNewRole(r);
          setRole(roleValue(r));
          setErrors((e) => ({ ...e, role: undefined }));
          toast.show({ tone: "success", title: t("team.customRole.saved", { name: r.name }) });
        }}
      />
    </Dialog>
  );
}

// ---------------------------------------------------------------- roles

/** Every role on one compact list: what it is for, how much it can do, its people (owner view). */
function RolesPanel({
  orgId,
  roles,
  members,
  loading,
}: {
  orgId: string;
  roles: Role[];
  members: Member[];
  loading: boolean;
}) {
  const toast = useToast();
  const [editing, setEditing] = useState<Role | null>(null);
  const [creating, setCreating] = useState(false);
  const most = Math.max(1, ...roles.map((r) => r.permissions.length));
  const isCustom = (r: Role) => r.key === "custom" && !r.is_system;
  // Owner first, then the presets, then the shop's own roles.
  const list = [...roles].sort(
    (a, b) =>
      Number(b.key === "owner") - Number(a.key === "owner") ||
      Number(isCustom(a)) - Number(isCustom(b)),
  );
  return (
    <section className="flex flex-col gap-3 pt-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 text-xs text-fg-secondary">{t("settings.team.roles.help")}</p>
        <Button
          icon="plus"
          variant="secondary"
          onClick={() => setCreating(true)}
          data-testid="team-new-role"
        >
          {t("team.newRole")}
        </Button>
      </div>
      <BusyRegion busy={loading} skeleton={<SkeletonText lines={4} />}>
        <div className={LIST}>
          <div className={cx(HEAD, ROLE_COLS)} aria-hidden="true">
            <span>{t("team.cols.role")}</span>
            <span>{t("team.cols.type")}</span>
            <span>{t("team.cols.access")}</span>
            <span>{t("team.cols.people")}</span>
            <span className="text-end">{t("team.cols.actions")}</span>
          </div>
          <ul
            aria-label={t("settings.team.roles.title")}
            className="flex flex-col divide-y divide-subtle"
          >
            {list.map((r) => {
              const people = members.filter((m) => m.role.id === r.id && m.status === "active");
              const custom = isCustom(r);
              return (
                <li key={r.id} data-role={r.id} className={cx(ROW, ROLE_COLS)}>
                  <div className="flex min-w-0 flex-col">
                    <span
                      className="truncate text-sm font-semibold"
                      data-content={custom ? "user" : undefined}
                    >
                      {roleLabel(r)}
                    </span>
                    <span className="text-2xs text-fg-tertiary">
                      {i18n.tDynamic(
                        `settings.team.roles.about.${r.key}`,
                        "settings.team.roles.about.custom",
                      )}
                    </span>
                  </div>
                  <Cell label={t("team.cols.type")}>
                    <Badge tone={custom ? "action" : "neutral"}>
                      {custom ? t("team.roleCustom") : t("team.rolePreset")}
                    </Badge>
                  </Cell>
                  <Cell label={t("team.cols.access")}>
                    <span className="flex items-center gap-2">
                      <span className="h-1.5 w-full max-w-28 overflow-hidden rounded-full bg-sunken">
                        <span
                          className="ady-bg-brand block h-full rounded-full"
                          ref={(el) =>
                            el?.style.setProperty(
                              "width",
                              `${Math.round((r.permissions.length / most) * 100)}%`,
                            )
                          }
                        />
                      </span>
                      <span className="ady-ledger flex-none text-2xs text-fg-tertiary">
                        {t("team.permCount", { count: r.permissions.length })}
                      </span>
                    </span>
                  </Cell>
                  <Cell label={t("team.cols.people")}>
                    <span className="ady-ledger text-sm">{people.length}</span>
                  </Cell>
                  <div className="flex md:justify-end">
                    {custom ? (
                      <Button
                        variant="ghost"
                        size="md"
                        icon="shield"
                        onClick={() => setEditing(r)}
                        data-testid="role-edit-permissions"
                      >
                        {t("team.customRole.editShort")}
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </BusyRegion>
      <Note>{t("settings.team.roles.note")}</Note>
      <CustomRoleDialog
        open={editing !== null || creating}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        orgId={orgId}
        role={editing}
        onCreated={(r) =>
          toast.show({
            tone: "success",
            title: editing
              ? t("team.customRole.updated", { name: r.name })
              : t("team.customRole.saved", { name: r.name }),
          })
        }
      />
    </section>
  );
}
